/* Offer assignment belongs to Shoplift's rendered theme, never to a UTM resolver. */
(function (scope) {
  'use strict';
  const ATTRIBUTE = '_new_customer_offer';
  const POPUP_KEY = 'onebone:welcome-popup';
  const hasGift = (cart, config) => (cart?.items || []).some(item =>
    String(item.product_id) === String(config.giftProductId) &&
    (!config.giftVariantIds?.length || config.giftVariantIds.map(String).includes(String(item.variant_id))) &&
    typeof item.properties?._gwp === 'string' && item.properties._gwp !== '');
  function createController(options) {
    const config = options.config;
    if (!['percent', 'gift'].includes(config.assignedArm)) throw Error('Unknown theme offer');
    let cart = options.cart || {items: [], attributes: {}}, sync = 'checking', inFlight;
    const available = () => config.enabled !== false && Boolean(config.giftProductId) && Number(config.percentOff) === 20;
    function getState() {
      const gift = hasGift(cart, config);
      return {assignedArm: config.assignedArm, effectiveArm: gift ? 'gift' : config.assignedArm,
        hasGift: gift, returning: Boolean(config.returningCustomer), sync,
        percentAvailable: available(), hasItems: (cart.items || []).length > 0,
        checkoutReady: sync === 'ready' && cart.attributes?.[ATTRIBUTE] === config.assignedArm,
        modalArm: gift || config.enabled === false || config.loggedInCustomer || config.returningCustomer ||
          (config.assignedArm === 'percent' && !available()) || !['ready', 'deferred'].includes(sync)
          ? null : config.assignedArm};
    }
    const emit = () => options.notify?.(getState());
    function accept(next) { cart = next; emit(); }
    async function request(url, init = {}) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), config.requestTimeoutMs || 8000);
      try {
        const response = await options.fetch(url, {...init, credentials: 'same-origin', signal: abort.signal});
        if (!response.ok) throw Error('Cart request failed');
        const next = await response.json();
        if (!Array.isArray(next.items) || typeof next.attributes !== 'object' || !next.attributes) throw Error('Invalid cart response');
        return next;
      } finally { clearTimeout(timeout); }
    }
    async function ensureSynced() {
      if (inFlight) return inFlight;
      inFlight = (async () => {
        try {
          sync = 'checking'; emit();
          cart = await request(config.cartReadUrl);
          if (config.assignedArm === 'percent' && !available()) { sync = 'unavailable'; emit(); return false; }
          for (let attempt = 0; attempt < 2; attempt++) {
            if (cart.attributes[ATTRIBUTE] === config.assignedArm) { sync = 'ready'; emit(); return true; }
            sync = 'pending'; emit();
            // Patch one attribute only. Never remove a line or rewrite another app's attributes.
            cart = await request(config.cartUpdateUrl, {method: 'POST', headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({attributes: {[ATTRIBUTE]: config.assignedArm}})});
            if (cart.attributes[ATTRIBUTE] === config.assignedArm) { sync = 'ready'; emit(); return true; }
            if (!cart.items.length) { sync = 'deferred'; emit(); return false; }
          }
          sync = 'error'; emit(); return false;
        } catch (_) { sync = 'error'; emit(); return false; }
      })();
      try { return await inFlight; } finally { inFlight = null; }
    }
    emit();
    return {getState, ensureSynced, accept};
  }
  function createPopup(options) {
    let opened = false, pending = false, submitted = false;
    const read = () => { try { return JSON.parse(options.storage.getItem(POPUP_KEY) || '{}'); } catch (_) { return null; } };
    const save = value => { try { options.storage.setItem(POPUP_KEY, JSON.stringify(value)); } catch (_) { /* Stay suppressed for this document. */ } };
    async function trigger() {
      if (!options.config.popupEnabled || options.config.designMode || opened || pending || submitted) return false;
      const memory = read();
      // If suppression cannot be read, do not bypass the form's visitor targeting.
      if (!memory || memory.submitted || Number(memory.closedUntil) > options.now()) return false;
      const state = options.state();
      if (state.modalArm !== options.config.assignedArm) return false;
      pending = true;
      try {
        // Public documented API. Missing/unknown identity fails closed; no cookie parsing or PII copying.
        const identified = await options.isIdentified();
        if (identified !== false || options.state().modalArm !== options.config.assignedArm) return false;
        const latest = read();
        if (!latest || latest.submitted || Number(latest.closedUntil) > options.now()) return false;
        opened = true;
        options.open(options.config.formId);
        return true;
      } catch (_) { return false; } finally { pending = false; }
    }
    function activity(detail) {
      if (!['XpS7MN', 'SnJK7a'].includes(detail?.formId)) return;
      if (detail.type === 'submit') { submitted = true; save({submitted: true}); }
      if (detail.type === 'close' && !submitted) save({...read(), closedUntil: options.now() + 5 * 86400000});
    }
    return {trigger, activity};
  }
  const api = {ATTRIBUTE, POPUP_KEY, hasGift, createController, createPopup};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document) return;
  const document = scope.document, node = document.getElementById('OfferThemeConfig');
  if (!node || scope.OneBoneOfferTheme) return;
  const config = JSON.parse(node.textContent), html = document.documentElement;
  const messages = config.messages;
  function refresh(state) {
    html.dataset.offerSync = state.sync;
    html.dataset.themeOffer = state.assignedArm;
    html.dataset.offerGiftPresent = String(state.hasGift);
    let message = state.sync === 'error' ? messages.retry
      : state.sync === 'unavailable' ? messages.unavailable
      : state.returning ? messages.returning
      : state.hasGift && state.assignedArm === 'percent' ? messages.giftWins
      : state.sync === 'ready' || state.sync === 'deferred' ? (state.assignedArm === 'gift' ? '' : messages[state.assignedArm])
      : messages.saving;
    document.querySelectorAll('[data-offer-status]').forEach(root => {
      root.hidden = !message;
      root.querySelector('[data-offer-status-text]').textContent = message;
      root.querySelector('[data-offer-retry]').hidden = !['error', 'unavailable'].includes(state.sync);
    });
    document.dispatchEvent(new CustomEvent('onebone-offer:changed', {detail: state}));
  }
  const controller = createController({config, cart: config.cart, fetch: scope.fetch.bind(scope), notify: refresh});
  scope.OneBoneOfferTheme = controller;
  if (config.designMode) return;
  let bypass = false;
  const capture = async (event, target) => {
    event.preventDefault(); event.stopImmediatePropagation();
    if (await controller.ensureSynced()) {
      bypass = true;
      try { event.type === 'submit' ? event.target.requestSubmit(target) : target.click(); }
      finally { bypass = false; }
    }
  };
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-offer-retry]')) { event.preventDefault(); controller.ensureSynced(); return; }
    const target = event.target.closest('[name="checkout"],a[href*="/checkout"]');
    if (target && !bypass) capture(event, target);
  }, true);
  document.addEventListener('submit', event => {
    if (!bypass && event.submitter?.name === 'checkout') capture(event, event.submitter);
  }, true);
  document.addEventListener('eurus:cart:items-changed', () => { if (document.visibilityState !== 'hidden') controller.ensureSynced(); });
  scope.addEventListener('pageshow', () => controller.ensureSynced());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') controller.ensureSynced(); });
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-offer-status]') || n.querySelector?.('[data-offer-status]'))))) refresh(controller.getState());
  });
  observer.observe(document.body, {childList: true, subtree: true});
  controller.ensureSynced();
  const popup = createPopup({config, state: controller.getState, now: Date.now,
    storage: {getItem: key => scope.localStorage.getItem(key), setItem: (key, value) => scope.localStorage.setItem(key, value)},
    isIdentified: () => typeof scope.klaviyo?.isIdentified === 'function' ? scope.klaviyo.isIdentified() : null,
    open: id => { scope._klOnsite = scope._klOnsite || []; scope._klOnsite.push(['openForm', id]); }});
  scope.addEventListener('klaviyoForms', event => popup.activity(event.detail));
  if (config.popupEnabled) {
    let scrollIntent = false, attempts = 0;
    const threshold = scope.matchMedia('(max-width: 767px)').matches ? 600 : 900;
    const attempt = () => { if (scrollIntent) popup.trigger(); };
    scope.addEventListener('scroll', () => { scrollIntent = scrollIntent || scope.scrollY >= threshold; attempt(); }, {passive: true});
    document.addEventListener('onebone-offer:changed', attempt);
    // Let a deferred integration load; never queue an unverified open before identity is available.
    const timer = setInterval(() => { attempt(); if (++attempts >= 60) clearInterval(timer); }, 500);
  }
})(typeof window !== 'undefined' ? window : globalThis);
